import * as React from 'react'
import { ReactElement, useContext, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { COMMAND_PREFIX } from './Commands'
import { useHistory } from 'react-router-dom'
import { taskBaseUrl } from './Tasks'
import { MenuItemKey } from '../components/MainMenu'
import { AppAction } from '../config/appConfig'
import { FileAction } from '../config/fileConfig'
import useUser from '../hooks/useUser'
import { ModuleType } from '../context/GlobalContext'
import useModules from '../hooks/module/useModules'
import useRoles, { rolesTasks, rolesTimes } from '../hooks/useRoles'
import { useSettings } from '../hooks/useSettings'
import { useTranslation } from 'react-i18next'
import useTaskStatuses from '../hooks/useTaskStatuses'
import {
    bundleIcon,
    ClipboardTaskAddFilled,
    ClipboardTaskAddRegular,
    TabDesktopSearchFilled,
    TabDesktopSearchRegular,
    TimerFilled,
    TimerRegular,
} from '@fluentui/react-icons'
import { makeStyles, shorthands, Text, tokens, Toolbar, ToolbarButton } from '@fluentui/react-components'
import BaseTaskOverview, { TaskFilterType } from './Task/BaseTaskOverview'
import BaseFileOverview from './File/BaseFileOverview'
import PopupContext from '../context/PopupContext'
import { DateTime } from 'luxon'
import { formatTimeInMinutes, parseTimeInMinutes } from '../helpers/numberHelper'
import { useTimeDivisorSetting } from '../hooks/file/useTimeDivisorSetting'

const AddTime = bundleIcon(TimerFilled, TimerRegular)
const SearchFile = bundleIcon(TabDesktopSearchFilled, TabDesktopSearchRegular)
const AddTask = bundleIcon(ClipboardTaskAddFilled, ClipboardTaskAddRegular)

const useStyles = makeStyles({
    columnContainer: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalL),
    },
    toolbar: {
        alignSelf: 'center',
    },
    title: {
        paddingLeft: tokens.spacingHorizontalS,
    },
})

export default function Dashboard(): ReactElement {
    const dispatch = useDispatch()
    const history = useHistory()
    const modules = useModules()
    const roles = useRoles()
    const { settings } = useSettings()
    const styles = useStyles()
    const timeDivisorSetting = useTimeDivisorSetting()
    const { t: translate } = useTranslation()
    const { currentUser } = useUser()
    const hasTaskAuthorization = useMemo<boolean>(() => roles.any(rolesTasks), [roles.loaded])
    const hasTimeAuthorization = useMemo<boolean>(() => roles.any(rolesTimes), [roles.loaded])
    const showRecentFiles = useMemo(() => settings?.dashboard?.showRecentFiles !== false, [settings])
    const { loaded: tasksLoaded, getStartStatus } = useTaskStatuses()
    const popupContext = useContext(PopupContext)

    const assigneeFilterValues = useMemo<Partial<TaskFilterType>>(
        () => ({
            assigners: [],
            assignees: [currentUser?.id],
            statuses: [getStartStatus().alpha],
            from: undefined,
            to: undefined,
        }),
        [tasksLoaded],
    )

    const assignerFilterValues = useMemo<Partial<TaskFilterType>>(
        () => ({
            assigners: [currentUser?.id],
            assignees: [],
            notAssignees: [currentUser?.id],
            statuses: [getStartStatus().alpha],
            from: undefined,
            to: undefined,
        }),
        [tasksLoaded],
    )

    // Once, on mount: `props` as a dependency re-dispatched both actions on every render, which turns any state
    // that observes them into a render loop (HAM-4295).
    useEffect(() => {
        dispatch({ type: AppAction.SetBreadcrumbs, payload: [MenuItemKey.Dashboard] })
        dispatch({ type: FileAction.ResetCurrent })
    }, [])

    return (
        <>
            <div className={styles.columnContainer}>
                <Toolbar className={styles.toolbar}>
                    {modules.has(ModuleType.TimeTracking) && hasTimeAuthorization && (
                        <ToolbarButton
                            vertical
                            icon={<AddTime />}
                            onClick={() =>
                                popupContext.setShowTimeTrackingDialog({
                                    userId: currentUser?.id,
                                    date: DateTime.now().toFormat('yyyy-LL-dd'),
                                    duration: formatTimeInMinutes(parseTimeInMinutes(timeDivisorSetting.initialDuration)),
                                })
                            }
                        >
                            {translate('action.create.time.title')}
                        </ToolbarButton>
                    )}
                    <ToolbarButton vertical icon={<SearchFile />} onClick={() => history.push(`${COMMAND_PREFIX}/search-file`)}>
                        {translate('action.search.file.title')}
                    </ToolbarButton>
                    {modules.has(ModuleType.Tasks) && hasTaskAuthorization && (
                        <ToolbarButton vertical icon={<AddTask />} onClick={() => popupContext.setShowTaskDialog({})}>
                            {translate('action.create.task.title')}
                        </ToolbarButton>
                    )}
                </Toolbar>

                {modules.has(ModuleType.Tasks) && hasTaskAuthorization && (
                    <>
                        <Text className={styles.title} size={600}>
                            {translate('dashboard.task.assignee.title')}
                        </Text>
                        <BaseTaskOverview
                            baseUrl={taskBaseUrl}
                            batchSizeItems={20}
                            maxCount={20}
                            fixedFilterValues={assigneeFilterValues}
                            filterContent={undefined}
                            headerActionsButtons={undefined}
                            localStorageKeyPrefix={undefined}
                        />
                        <Text className={styles.title} size={600}>
                            {translate('dashboard.task.assigner.title')}
                        </Text>
                        <BaseTaskOverview
                            baseUrl={taskBaseUrl}
                            batchSizeItems={20}
                            maxCount={20}
                            fixedFilterValues={assignerFilterValues}
                            filterContent={undefined}
                            headerActionsButtons={undefined}
                            localStorageKeyPrefix={undefined}
                        />
                    </>
                )}
                {showRecentFiles && (
                    <>
                        <Text className={styles.title} size={600}>
                            {translate('dashboard.file.title')}
                        </Text>
                        <BaseFileOverview
                            batchSizeItems={20}
                            maxCount={20}
                            filterContent={undefined}
                            headerActionsButtons={undefined}
                            localStorageKeyPrefix={undefined}
                            listColumnFullWidth
                        />
                    </>
                )}
            </div>
        </>
    )
}
